import {
  ANALYTICS_EVENTS,
  FORM_EVENT_ACTIONS,
  FORM_EVENT_CATEGORIES,
  FORM_TYPES,
} from './constants';
import { pushToDataLayer } from './dataLayer';
import { buildFormEventPayload, buildLegacyFormMeta } from './builders/formPayload';

const trackFormEvent = ({ target, ...payloadOptions } = {}) => {
  const payload = buildFormEventPayload(payloadOptions);
  return pushToDataLayer(payload, { target });
};

export const isAnalyticsEnabled = (analytics = {}) => Boolean(analytics?.enabled);

export const getFormTrackingAction = (formVariant) =>
  formVariant === 'paginated'
    ? FORM_EVENT_ACTIONS.MULTI_STEP_SUBMIT
    : FORM_EVENT_ACTIONS.SINGLE_STEP_SUBMIT;

export const getFormTrackingType = (formVariant) =>
  formVariant === 'paginated' ? FORM_TYPES.MULTI_STEP : FORM_TYPES.SINGLE_STEP;

export const getSubmissionEventLabel = ({ formVariant, placement } = {}) =>
  formVariant === 'paginated' ? 'activeProspect' : `Form placement: ${placement || 'unspecified'}`;

export const getProgressEventLabel = ({ stepName, stepIndex } = {}) =>
  stepName || `step${stepIndex}`;

export const trackFormSubmission = ({
  target,
  analytics,
  campaign,
  storedPhone,
  defaultCampaignNumber,
  formVariant,
  formData,
  meta,
  eventId,
} = {}) =>
  trackFormEvent({
    target,
    event: ANALYTICS_EVENTS.FORM_SUBMISSION,
    eventCategory: FORM_EVENT_CATEGORIES.SUCCESS,
    eventAction: getFormTrackingAction(formVariant),
    eventLabel: getSubmissionEventLabel({
      formVariant,
      placement: analytics?.placement,
    }),
    meta: buildLegacyFormMeta({
      formData,
      analytics,
      campaign,
      storedPhone,
      defaultCampaignNumber,
      formType: getFormTrackingType(formVariant),
      meta,
      eventId,
      includeFormData: true,
      includeAnalyticsMeta: true,
    }),
  });

export const trackFormError = ({
  target,
  analytics,
  campaign,
  storedPhone,
  defaultCampaignNumber,
  formVariant,
  formData,
  errorMessage,
  meta,
  eventId,
} = {}) =>
  trackFormEvent({
    target,
    event: ANALYTICS_EVENTS.FORM_ERROR,
    eventCategory: FORM_EVENT_CATEGORIES.ERROR,
    eventAction: getFormTrackingAction(formVariant),
    eventLabel: errorMessage,
    meta: buildLegacyFormMeta({
      analytics,
      campaign,
      storedPhone,
      defaultCampaignNumber,
      formType: getFormTrackingType(formVariant),
      error: errorMessage,
      meta,
      eventId,
      includeFormData: false,
      includeAnalyticsMeta: true,
    }),
  });

export const trackFormProgress = ({
  target,
  analytics,
  campaign,
  storedPhone,
  defaultCampaignNumber,
  formVariant,
  stepName,
  stepIndex,
  meta,
  eventId,
} = {}) =>
  trackFormEvent({
    target,
    event: ANALYTICS_EVENTS.FORM_PROGRESS,
    eventCategory: FORM_EVENT_CATEGORIES.MULTI_STEP,
    eventAction: FORM_EVENT_ACTIONS.PROGRESS,
    eventLabel: getProgressEventLabel({ stepName, stepIndex }),
    meta: buildLegacyFormMeta({
      analytics,
      campaign,
      storedPhone,
      defaultCampaignNumber,
      formType: getFormTrackingType(formVariant),
      meta,
      eventId,
      includeFormData: false,
      includeAnalyticsMeta: true,
    }),
  });

export const trackFormInteraction = ({
  target,
  fieldName,
  fieldType,
  fieldLabel,
  eventValue,
  meta,
} = {}) =>
  trackFormEvent({
    target,
    event: ANALYTICS_EVENTS.FORM_INTERACTION,
    eventAction:
      fieldType === 'checkbox' ? FORM_EVENT_ACTIONS.CHECKBOX_CLICK : FORM_EVENT_ACTIONS.RADIO_CLICK,
    eventLabel: fieldLabel || fieldName,
    eventValue,
    meta,
  });
